import {
  APP_STORE_URL,
  CURRENT_URL,
  isFabricDev,
  isFabricProd,
  PLAY_STORE_URL,
} from '../constants/env';
import inNextServer from '../utils/next';
import { isInDesktop } from '../hooks/todesktop';
import { Capacitor } from '@capacitor/core';
import { getVersions } from '../hooks/useVersionCheck';
import {
  defaultCompatConfig,
  setupCompatConfig,
  type UseDefaultResourceClickHandler,
} from '@fabric-app/compat';
import { downloadMobile, downloadWeb } from '../hooks/useDownloadFdoc';
import { database } from './storage/global';
import Image from 'next/image';
import Link from 'next/link';
import { useAuthStatus, useAuthUser } from '../hooks/auth';
import { platform } from '@todesktop/client-core';
import { AppLauncher } from '@capacitor/app-launcher';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser';
import {
  getCompatPlatform,
  getSimplifiedPlatform,
  isInCapacitorPlatform,
  isInDesktopPlatform,
  isInFabricMobilePlatform,
} from '@fabric-app/compat/capabilities/platform';
import { WOODY_API_URL } from '../services/woody/woody';
import { useShowExpandedResource } from '@fabric-app/business/resources/stores/useExpandedResourceStore';
import { KeepAwake } from '@capacitor-community/keep-awake';

import {
  captureException,
  captureMessage,
  captureUnderscoreErrorException,
  withScope,
  setTag,
  reactRouterV4BrowserTracingIntegration,
  addBreadcrumb,
} from '@sentry/nextjs';
import { useCallback, useContext, useMemo } from 'react';
import { ReadFile } from '@/mobile/readfile';
import { fabricMobileAsync } from '../modules/mobile/utils/fabricMobileAsync';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import { useRouter } from 'next/router';
import { omitProperties, sanitizeForLogging } from '@fabric-app/utils/object/manipulation';
import { dispatchFabricEvent } from '@fabric-app/business/hooks/useFabricEvent';
import { setupLogger } from '@fabric-app/utils/logging';
import { generatePath } from '@fabric-app/utils/url/routing';
import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import { RootType } from '@fabric/woody-client';
import { logTrackerEvent, logTrackerIssue } from './or';
import { isString } from '@fabric-app/utils/guards';

export const setupCompatServer = () => {
  setupCompatConfig({
    ENVIRONMENT: {
      DASHBOARD_URL: CURRENT_URL,
      ANALYTICS: {
        DISABLE_PERSISTENCE: false,
        TRACK_PAGEVIEW: true,
        MIXPANEL_TOKEN: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN!,
      },
      APP_STORE_URL: APP_STORE_URL,
      CDN_URL: process.env.NEXT_PUBLIC_FABRIC_CDN_URL || 'https://cdn.fabric.so',
      PLAY_STORE_URL: PLAY_STORE_URL,
      isFabricDev: isFabricDev,
      isFabricProd: isFabricProd,
      ENVIRONMENT: process.env.REACT_APP_ENV!,
      PRICING: {
        BASIC_MONTHLY_PLAN_ID: process.env.NEXT_PUBLIC_STRIPE_BASIC_MONTHLY_PLAN_ID!,
        BASIC_YEARLY_PLAN_ID: process.env.NEXT_PUBLIC_STRIPE_BASIC_YEARLY_PLAN_ID!,
        BASIC_OFFERING_ID: process.env.NEXT_PUBLIC_REVENUECAT_BASIC_OFFERING!,
        PRO_MONTHLY_PLAN_ID: process.env.NEXT_PUBLIC_STRIPE_PRO_MONTHLY_PLAN_ID!,
        PRO_YEARLY_PLAN_ID: process.env.NEXT_PUBLIC_STRIPE_PRO_YEARLY_PLAN_ID!,
        PRO_OFFERING_ID: process.env.NEXT_PUBLIC_REVENUECAT_PRO_OFFERING!,
        MAX_MONTHLY_PLAN_ID: process.env.NEXT_PUBLIC_STRIPE_MAX_MONTHLY_PLAN_ID!,
        MAX_YEARLY_PLAN_ID: process.env.NEXT_PUBLIC_STRIPE_MAX_YEARLY_PLAN_ID!,
        MAX_OFFERING_ID: process.env.NEXT_PUBLIC_REVENUECAT_MAX_OFFERING!,
        LIFETIME_BELIEVER_PLAN_ID: process.env.NEXT_PUBLIC_STRIPE_LIFETIME_BELIEVER_PLAN_ID!,
      },
      SOCKETIO_LOG_EVENTS: process.env.NEXT_PUBLIC_SOCKETIO_LOG_EVENTS === 'true',
      TWITTER_EMBED_WIDGET_JS_URL: 'https://platform.x.com/widgets.js',
      WOODY_API_URL: WOODY_API_URL!,
      MOBILE_DEEPLINK_URL: CURRENT_URL,
    },

    UI: {
      SHOW_BRAND_MORE: false,
      useOnExpandedResource: () => {
        const { replace, query, push } = useRouter();

        return useCallback(
          (resource, options) => {
            const resourceId = typeof resource === 'string' ? resource : resource?.id;

            const nextQuery = omitProperties(
              {
                ...query,
                expandedFdocId: resourceId,
                ...options?.queryParams,
                t: options?.queryParams?.t || undefined,
                new: undefined,
              },
              [omitProperties.isNullOrUndefined, omitProperties.isEmptyArray],
            );

            /**
             * if opening, we use push
             * if navigating between for example, we use replace (there's existing expandedFdocId in the query)
             *
             * this is so when user goes back with e.g. gesture, back will just close the modal
             * instead of going back further in the history
             */
            (query.expandedFdocId ? replace : push)({
              query: nextQuery,
            });

            return false;
          },
          [replace, query, push],
        );
      },
    },
    PLATFORM: {
      getPlatform: () => {
        if (inNextServer()) return 'server';
        if (isInDesktop()) return 'desktop';
        if (Capacitor.isNativePlatform()) {
          if (Capacitor.getPlatform() === 'ios') return 'capacitor-ios';
          if (Capacitor.getPlatform() === 'android') return 'capacitor-android';
        }
        if (window.fabricMobile?.isSheet) {
          if (window.fabricMobile.os === 'iOS') return 'fabric-mobile-ios';
          if (window.fabricMobile.os === 'Android') return 'fabric-mobile-android';
        }
        return 'web';
      },

      getPlatformVersion: async () => {
        const versions = await getVersions();

        return {
          webVersion: versions.webVersion!,
          appVersion: versions.appVersion ?? versions.desktopVersion,
          app: versions.desktopVersion
            ? {
                id: 'fabric-desktop',
                name: 'Fabric Desktop',
                version: versions.desktopVersion,
              }
            : versions.appInfo,
        };
      },
      getWebVersion: () => {
        return process.env.NEXT_PUBLIC_APP_VERSION!;
      },
      getSimplifiedPlatform: () => getSimplifiedPlatform(getCompatPlatform().getPlatform()),
      openExternal: (url) => {
        if (isInDesktopPlatform()) {
          platform.os.openURL(url);
          return true;
        }

        // TODO: remove this when 0.4.3 (3) is released to users.
        // For versions below or equal to 0.4.2 (15) it has an outdated InAppBrowser plugin that doesn't work on iOS.
        if (isInCapacitorPlatform('capacitor-ios')) {
          AppLauncher.openUrl({ url });
          return true;
        }

        // TODO: Update this to use the official Capacitor plugin.
        // @see: https://capacitorjs.com/docs/apis/inappbrowser
        // At the time we added the cordova plugin, capacitor was still at v5 and didn't have the inappbrowser plugin.
        if (isInCapacitorPlatform()) {
          InAppBrowser.create(url, '_system');
          return true;
        }

        return false;
      },
    },
    SENTRY: {
      captureException,
      captureMessage,
      captureUnderscoreErrorException,
      withScope,
      setTag,
      reactRouterV4BrowserTracingIntegration,
    },

    FILE: {
      getIsAvailable: () => isInCapacitorPlatform() || isInFabricMobilePlatform(),
      readFile(options) {
        if (isInCapacitorPlatform()) {
          return ReadFile.readFile(options);
        } else if (isInFabricMobilePlatform()) {
          return fabricMobileAsync('readFile', {
            maxBytes: options.maxBytes ?? null,
            url: options.url,
          });
        }

        throw new Error('File service not found');
      },

      uploadFile(options, callback) {
        if (isInCapacitorPlatform()) {
          return ReadFile.uploadFile(options, callback);
        } else if (isInFabricMobilePlatform()) {
          fabricMobileAsync('uploadFile', options)
            .then((val) => callback(val))
            .catch((e) => callback(null, e));
          return Promise.resolve('');
        }

        throw new Error('File service not found');
      },
    },

    WAKE_LOCK: {
      lock: async () => {
        if (isInCapacitorPlatform() && Capacitor.isPluginAvailable('KeepAwake')) {
          return KeepAwake.keepAwake();
        }

        return defaultCompatConfig.WAKE_LOCK.lock();
      },
      release: async () => {
        if (isInCapacitorPlatform() && Capacitor.isPluginAvailable('KeepAwake')) {
          return KeepAwake.allowSleep();
        }
        return defaultCompatConfig.WAKE_LOCK.release();
      },
      isLocked: async () => {
        if (isInCapacitorPlatform() && Capacitor.isPluginAvailable('KeepAwake')) {
          return (await KeepAwake.isKeptAwake()).isKeptAwake;
        }
        return defaultCompatConfig.WAKE_LOCK.isLocked();
      },
      isSupported: async () => {
        if (isInCapacitorPlatform() && Capacitor.isPluginAvailable('KeepAwake')) {
          return (await KeepAwake.isSupported()).isSupported;
        }
        return defaultCompatConfig.WAKE_LOCK.isSupported();
      },
    },

    getDatabase: () => database,
    getLocation: () => getCompatPlatform().getPlatform(),
    getVersions,
    inNextServer,
    useRouter: () => {
      const router = useContext(RouterContext);
      if (!router) {
        throw new Error('Next router was not mounted.');
      }
      return router;
    },
    isInDesktop: () => isInDesktopPlatform(),
    isInMobile: () => isInCapacitorPlatform(),
    useAuthStatus,
    useAuthUser,
    getGlobalEventTarget: () => window,
  });

  setupLogger({
    isDebugEnabled: process.env.NEXT_PUBLIC_DEBUG_ENABLED === 'true',
    isDev: isFabricDev,
    on: (prefix, key, params) => {
      const sanitizedParams = sanitizeForLogging(
        params,
        key === 'error' || key === 'warn'
          ? {
              // errors and warns are rarer and more purposeful, and we want to be able to get more context, so we increase the limits
              maxArrayLength: 100,
              maxObjectKeys: 100,
              maxDepth: 30,
              maxStringLength: 10000,
            }
          : undefined,
      );

      const kind = `${key.toUpperCase()}-[${prefix}]`;

      if (key === 'error') {
        logTrackerIssue({
          kind,
          payload: {
            key,
            params: sanitizedParams,
          },
        });
      } else {
        logTrackerEvent({
          kind,
          payload: {
            key,
            params: sanitizedParams,
          },
        });

        addBreadcrumb({
          category: kind,
          data: {
            key,
            params: sanitizedParams,
          },
          message: sanitizedParams.map((param) => (isString(param) ? param : '')).join(' '),
        });
      }
    },
  });
};

const useDefaultResourceClickHandler: UseDefaultResourceClickHandler = () => {
  const { push } = useRouter();
  const showExpandedResource = useShowExpandedResource();

  return useMemo(
    () => ({
      defaultClickFolder: (folder) => {
        if (
          folder.root &&
          folder.root.type === RootType.SPACE &&
          folder.root.folder.id === folder.id
        ) {
          /**
           * WARN
           * this expects the folder to have accessible space/id
           * will not work e.g. for folders of mirrors
           */
          push(
            generatePath(appRoutes.space.detail, {
              spaceId: folder.id,
            }),
          );
        } else {
          push(`/folders/${folder.id}`);
        }
      },
      defaultClickResource: (resource) => {
        if (resource._meta?.externalData) {
          const url = resource._meta.externalData.url;
          if (url) {
            window.open(url, '_blank');
          }
          return;
        }

        showExpandedResource(resource);
      },
      defaultClickResourceId: (resourceId) => {
        showExpandedResource(resourceId);
      },
    }),
    [showExpandedResource, push],
  );
};

let ranOnce = false;
export const setupCompat = () => {
  if (ranOnce) return;
  ranOnce = true;

  setupCompatServer();
  setupCompatConfig({
    downloadFile: (fileUrl, title, contentType) => {
      if (isInCapacitorPlatform()) {
        return downloadMobile(fileUrl, title, contentType);
      } else {
        return downloadWeb(fileUrl, title, contentType);
      }
    },
    useDefaultResourceClickHandler,

    // @ts-expect-error - TODO: fix this
    Image,
    // @ts-expect-error - TODO: fix this
    Link,
    getDefaultRadixPortalContainer: () =>
      typeof document !== 'undefined' ? document.body : undefined,
    useRouter: () => {
      const router = useContext(RouterContext);
      if (!router) {
        throw new Error('Next router was not mounted.');
      }
      return router;
    },
    setSummarizeText: (text: string | undefined) => {
      if (!text) return;
      dispatchFabricEvent('fabric-assistant-summarize-text', { text });
    },
  });
};
